import { type Node as PMNode } from "prosemirror-model";
import { Plugin, PluginKey } from "prosemirror-state";

import { schema } from "@moment/prosemirror-schema";

export const preserveReadonlySectionPluginKey = new PluginKey("preserveReadonlySection");

interface ManagedSection {
	node: PMNode;
	from: number;
	to: number;
}

/**
 * Collect every moment-managed `readonly_section` node in a document. We match
 * on the `managedSection: true` attribute so only the moment-managed template
 * (currently AGENTS.md) is guarded; plain readonly sections are not protected.
 */
function collectManagedReadonlySections(doc: PMNode): ManagedSection[] {
	const readonlyType = schema.nodes.readonly_section;
	const result: ManagedSection[] = [];

	doc.forEach((node, offset) => {
		if (node.type === readonlyType && node.attrs["managedSection"] === true) {
			result.push({ node, from: offset, to: offset + node.nodeSize });
		}
	});

	return result;
}

function sameSections(before: ManagedSection[], after: ManagedSection[]): boolean {
	if (before.length !== after.length) return false;
	for (let i = 0; i < before.length; i++) {
		const b = before[i];
		const a = after[i];
		if (!b || !a) return false;
		if (!b.node.eq(a.node)) return false;
	}
	return true;
}

/**
 * ProseMirror plugin that preserves the moment-managed `readonly_section`
 * node (the template at the top of AGENTS.md) when user-authored transactions
 * would remove or mutate it.
 *
 * Rather than blocking specific key bindings (which is fragile — `Cmd-A`
 * followed by typing or Backspace replaces the selection *including* the
 * readonly section before any Backspace/Delete command runs), this plugin
 * inspects every committed transaction and appends a restoring transaction
 * whenever a managed section was dropped or mutated.
 *
 * Importantly, this *preserves* the rest of the user's edit. If a user
 * selects the whole document (which includes the managed section and their
 * own typed text) and presses Backspace, the user-typed content gets
 * deleted as expected and only the managed section is restored — rather
 * than the whole transaction being discarded.
 *
 * Transactions flagged with `externalChange` meta (applied by the file
 * watcher when the on-disk content changes) are always allowed through —
 * disk is the source of truth for the managed section, and that code path
 * is how moment itself legitimately rewrites the template.
 */
export const preserveReadonlySectionPlugin = () =>
	new Plugin({
		key: preserveReadonlySectionPluginKey,

		appendTransaction(transactions, oldState, newState) {
			if (!transactions.some((tr) => tr.docChanged)) return null;

			if (transactions.some((tr) => tr.getMeta("externalChange") === true)) {
				return null;
			}

			const oldManaged = collectManagedReadonlySections(oldState.doc);
			if (oldManaged.length === 0) return null;

			const newManaged = collectManagedReadonlySections(newState.doc);
			if (sameSections(oldManaged, newManaged)) return null;

			const tr = newState.tr;

			// Strip any surviving managed sections from the new doc (some may be
			// mutated copies left behind by the original transaction). Walk
			// backwards so earlier positions stay valid as we delete.
			for (let i = newManaged.length - 1; i >= 0; i--) {
				const section = newManaged[i];
				if (!section) continue;
				tr.delete(section.from, section.to);
			}

			// Re-insert the original managed sections at the top of the doc,
			// preserving their original order and attributes (including
			// rawContent). PM nodes are immutable, so re-using the instances
			// from oldState.doc is safe.
			const originals = oldManaged.map((m) => m.node);
			tr.insert(0, originals);

			tr.setMeta("addToHistory", false);
			tr.setMeta(preserveReadonlySectionPluginKey, { restored: true });

			return tr;
		},
	});
